import type { AnalyticsShortlinkDisposition } from "../../../api.ts";

export type ShortlinkIssueKind =
  | "healthy_attributed"
  | "healthy_cookie_reuse"
  | "healthy_per_ad"
  | "crawler"
  | "rate_limited"
  | "macro_fb_template"
  | "macro_tiktok_aid"
  | "macro_tiktok_clickid"
  | "macro_other"
  | "shortlink_timeout"
  | "shortlink_internal_error"
  | "pool_per_ad_degraded"
  | "fb_partial_degraded"
  | "delivery_exhausted"
  | "missing_ad_id"
  | "missing_fbclid_fb_paid"
  | "missing_fbclid_fb_referer"
  | "missing_click_id_other"
  | "unknown";

export interface ShortlinkIssueMeta {
  readonly label: string;
  readonly hint: string;
  readonly severity: "info" | "warn" | "error";
  readonly dispositionFilter: AnalyticsShortlinkDisposition | null;
}

export const SHORTLINK_ISSUE_META: Record<ShortlinkIssueKind, ShortlinkIssueMeta> = {
  healthy_attributed: {
    label: "正常归因",
    hint: "ad_id 与 click id 齐全，已分配独占链。",
    severity: "info",
    dispositionFilter: "attributed",
  },
  healthy_cookie_reuse: {
    label: "Cookie 复用",
    hint: "同用户重复点击，复用已有邀请链。",
    severity: "info",
    dispositionFilter: "cookie_reuse",
  },
  healthy_per_ad: {
    label: "Per-ad 主链",
    hint: "Google 投放按设计走 per-ad 共享链（一个广告一条），入群按广告 FIFO 认领 gclid 上报，正常状态。",
    severity: "info",
    dispositionFilter: "per_ad_primary",
  },
  crawler: {
    label: "爬虫/预览",
    hint: "Facebook/Telegram/WhatsApp 预览或爬虫 UA，非真人点击，可忽略。",
    severity: "info",
    dispositionFilter: "non_human",
  },
  rate_limited: {
    label: "限流跳过",
    hint: "同 IP 短时访问过多。已放宽至 60/min；排查刷量或继续调高 shortlinkRateLimitPerMin。",
    severity: "warn",
    dispositionFilter: "rate_limited",
  },
  macro_fb_template: {
    label: "FB 宏未替换",
    hint: "ad_id 仍为 {{ad.id}} 或 URL 编码形式。请在 FB Ads Manager 检查落地页 URL 模板。",
    severity: "error",
    dispositionFilter: "macro_invalid",
  },
  macro_tiktok_aid: {
    label: "TikTok AID 宏未替换",
    hint: "ad_id 仍为 __AID__。请在 TikTok Ads 检查 URL 参数宏。",
    severity: "error",
    dispositionFilter: "macro_invalid",
  },
  macro_tiktok_clickid: {
    label: "TikTok ClickID 宏未替换",
    hint: "含 __CLICKID__ / __CID__ 占位符。请检查 TikTok 落地页 URL 模板。",
    severity: "error",
    dispositionFilter: "macro_invalid",
  },
  macro_other: {
    label: "其他宏未替换",
    hint: "ad_id 含未解析占位符。检查各平台 URL 动态参数是否已在广告审核后生效。",
    severity: "error",
    dispositionFilter: "macro_invalid",
  },
  shortlink_timeout: {
    label: "短链处理超时",
    hint: "整条归因逻辑超过 20s 预算。已去掉热路径 TG 创建；若仍出现请检查 PG 延迟或对该 chat 预热池。",
    severity: "warn",
    dispositionFilter: "shared_fallback",
  },
  shortlink_internal_error: {
    label: "短链内部异常",
    hint: "DB/运行时异常导致降级 redirect。查看 api 日志 failureReason 字段。",
    severity: "error",
    dispositionFilter: "shared_fallback",
  },
  pool_per_ad_degraded: {
    label: "池空 per-ad 降级",
    hint: "独占池 claim 失败，已走 per-ad 共享链（仍可按 ad_id 上报）。请预热链接池或提高 pool_min_threshold。",
    severity: "warn",
    dispositionFilter: "per_ad_fallback",
  },
  fb_partial_degraded: {
    label: "FB 丢 fbclid（已降级）",
    hint: "Android WebView 常见：ad_id 有效但无 fbclid。系统已走 per-ad 共享链，可按 ad_id 上报（无 fbc）。",
    severity: "info",
    dispositionFilter: "per_ad_fallback",
  },
  delivery_exhausted: {
    label: "发链全失败",
    hint: "独占池与 per-ad 链均不可用，降级到 chat 共享兜底链，归因丢失。检查 TG 权限与兜底链配置。",
    severity: "error",
    dispositionFilter: "natural_fallback",
  },
  missing_ad_id: {
    label: "缺少 ad_id",
    hint: "访问 URL 无 ad_id 参数。检查广告落地页是否携带 ?ad_id= 宏。",
    severity: "warn",
    dispositionFilter: "natural_fallback",
  },
  missing_fbclid_fb_paid: {
    label: "FB 丢 fbclid（历史 utm paid）",
    hint: "历史数据：utm paid 但无 fbclid。新版本应走 per-ad 降级。",
    severity: "warn",
    dispositionFilter: "natural_fallback",
  },
  missing_fbclid_fb_referer: {
    label: "FB 丢 fbclid（referer）",
    hint: "referer 来自 facebook 但无 fbclid。新版本应走 per-ad 降级。",
    severity: "warn",
    dispositionFilter: "natural_fallback",
  },
  missing_click_id_other: {
    label: "缺少 click id（其他）",
    hint: "有 ad_id 但无平台 click id。检查 fbclid/ttclid/gclid 宏是否配置。",
    severity: "warn",
    dispositionFilter: "natural_fallback",
  },
  unknown: {
    label: "未知",
    hint: "未识别的处置类型。",
    severity: "info",
    dispositionFilter: null,
  },
};

export const issueSeverityColor = (severity: ShortlinkIssueMeta["severity"]): string => {
  switch (severity) {
    case "error":
      return "red";
    case "warn":
      return "orange";
    case "info":
      return "gray";
  }
};
